import React, { useState, useEffect } from 'react';
import { 
  BarChart3, MessageSquare, LogOut, ArrowLeft, ShieldCheck, 
  User, CheckCircle2 
} from 'lucide-react';
import { getStoredToken, getStoredUser, clearAuthData, getAdminMe } from './adminApi';
import AdminLogin from './AdminLogin';
import DashboardOverview from './DashboardOverview';
import SessionExplorer from './SessionExplorer';

export default function AdminPortal({ onReturnToChat }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'sessions'
  const [currentUsername, setCurrentUsername] = useState('admin');
  const [sessionSearchTerm, setSessionSearchTerm] = useState('');
  const [checkingAuth, setCheckingAuth] = useState(true);

  const verifyAuth = async () => {
    const token = getStoredToken();
    if (!token) {
      setIsAuthenticated(false);
      setCheckingAuth(false);
      return;
    }

    try {
      const me = await getAdminMe();
      setCurrentUsername(me.username || 'admin');
      setIsAuthenticated(true);
    } catch (_) {
      clearAuthData();
      setIsAuthenticated(false);
    } finally {
      setCheckingAuth(false);
    }
  };

  useEffect(() => {
    verifyAuth();

    const handleExternalLogout = () => {
      setIsAuthenticated(false);
    };
    window.addEventListener('tedra_auth_logout', handleExternalLogout);
    return () => window.removeEventListener('tedra_auth_logout', handleExternalLogout);
  }, []);

  const handleLoginSuccess = (username) => {
    setCurrentUsername(username);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    clearAuthData();
    setIsAuthenticated(false);
  };

  const handleSelectFaqFromOverview = (faqQuery) => {
    setSessionSearchTerm(faqQuery);
    setActiveTab('sessions');
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center text-white">
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          <span className="text-sm font-medium text-gray-300">Checking credentials...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <AdminLogin 
        onLoginSuccess={handleLoginSuccess} 
        onReturnToChat={onReturnToChat} 
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#f8faf9] flex flex-col font-sans">
      {/* Top Navbar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          {/* Logo & Brand */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-[#668f45] to-[#8ac35d] flex items-center justify-center text-white shadow-md shadow-[#668f45]/20">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-extrabold text-gray-900 tracking-tight text-base">TEDRA</span>
                <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 bg-[#668f45]/10 text-[#668f45] rounded-md tracking-wider">
                  Admin
                </span>
              </div>
              <p className="text-[11px] text-gray-400">ThirdEye Data • Conversation Intelligence</p>
            </div>
          </div>

          {/* Navigation Tabs */}
          <nav className="hidden md:flex items-center gap-1 bg-gray-100/80 p-1 rounded-xl">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                activeTab === 'overview'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <BarChart3 className="w-4 h-4 text-[#668f45]" />
              Analytics Overview
            </button>
            <button
              onClick={() => setActiveTab('sessions')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                activeTab === 'sessions'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <MessageSquare className="w-4 h-4 text-[#668f45]" />
              Conversations Explorer
            </button>
          </nav>

          {/* User profile, return to chat & logout */}
          <div className="flex items-center gap-3">
            {onReturnToChat && (
              <button
                onClick={onReturnToChat}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                title="Open Public Chatbot"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Chatbot
              </button>
            )}

            <div className="flex items-center gap-2 pl-3 border-l border-gray-200">
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-bold text-xs border border-gray-200">
                <User className="w-4 h-4 text-gray-600" />
              </div>
              <div className="hidden lg:block text-left">
                <p className="text-xs font-bold text-gray-900 leading-tight">{currentUsername}</p>
                <p className="text-[10px] text-gray-400 font-medium">Administrator</p>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
              title="Sign Out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Mobile Navigation bar */}
        <div className="md:hidden flex border-t border-gray-100 px-4 py-2 bg-gray-50/50 gap-2">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex-1 py-1.5 text-xs font-bold rounded-lg text-center ${
              activeTab === 'overview' ? 'bg-[#668f45] text-white' : 'text-gray-600'
            }`}
          >
            Analytics
          </button>
          <button
            onClick={() => setActiveTab('sessions')}
            className={`flex-1 py-1.5 text-xs font-bold rounded-lg text-center ${
              activeTab === 'sessions' ? 'bg-[#668f45] text-white' : 'text-gray-600'
            }`}
          >
            Conversations
          </button>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' ? (
          <DashboardOverview onSelectFaq={handleSelectFaqFromOverview} />
        ) : (
          <SessionExplorer 
            initialSearch={sessionSearchTerm} 
            onClearSearch={() => setSessionSearchTerm('')} 
          />
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white py-4 text-center text-xs text-gray-400">
        TEDRA Conversational AI System • ThirdEye Data Inc.
      </footer>
    </div>
  );
}
